import React, { useState, useEffect } from 'react';
import { TextBoxComponent } from '@syncfusion/ej2-react-inputs';
import './OpTextBox.css';
import clsx from 'clsx';

/**
 * OpTextBox - A wrapper around Syncfusion TextBoxComponent with extended UI and validation behavior.
 *
 * Provides a standardized input field with support for:
 * - Label rendering (top or inside positioning)
 * - Required indicator (asterisk)
 * - Error message display
 * - Disabled state styling
 * - Optional numeric-only input enforcement
 *
 * Features:
 * - Built on Syncfusion `TextBoxComponent`
 * - Manages its own internal state, meaning the parent does not need to feed the value back
 * - `value` acts as an initial preload
 * - `onChange` notifies the parent of changes
 *
 * Props:
 *
 * @param {boolean} [enabled=true] Controls whether the input is enabled or disabled.
 * @param {boolean} [enableNumbersOnly=false] Restricts input to numeric characters only (0-9).
 * @param {string} [errorMessage] Displays an error message below the input and applies error styling.
 * @param {string} label Text label displayed above or inside the input depending on `labelPosition`.
 * @param {'Top'|'Inside'} [labelPosition='Top'] Determines label placement relative to the input field.
 * @param {boolean} [showAsterisk] If true, displays a required field asterisk (*) next to the label.
 * @param {string} [value] Initial preload value of the input field.
 * @param {function} [onChange] Callback fired when the value changes. Receives the updated string value.
 * @param {object} textBoxParams Additional props passed directly to Syncfusion TextBoxComponent.
 */
export function OpTextBox({
  enabled = true,
  enableNumbersOnly = false,
  errorMessage,
  label,
  labelPosition = 'Top',
  showAsterisk,
  value,
  onChange,
  ...textBoxParams
}) {
  const asterisk = showAsterisk ? '*' : '';
  const isDisabled = !enabled;
  const isLabelInside = labelPosition === 'Inside';

  // Manage internal state so the parent isn't forced to control the component
  const [internalValue, setInternalValue] = useState(value ?? '');

  // Sync internal state if the external value (preload) changes (e.g. from an async API call)
  useEffect(() => {
    if (value !== undefined) {
      setInternalValue(value);
    }
  }, [value]);

  const handleInput = (args) => {
    let currentValue = args.value; // Syncfusion exposes the typed value here

    // Handle numeric restriction
    if (enableNumbersOnly) {
      currentValue = currentValue?.replace(/\D/g, '');
      // Update the raw DOM event immediately to prevent visual flickering of letters
      if (args.event?.target) {
        args.event.target.value = currentValue;
      }
    }

    // Update local state to render changes
    setInternalValue(currentValue);

    // Pass the current value up to the caller
    if (onChange) {
      onChange(currentValue);
    }

    // Preserve any existing input handler passed through textBoxParams
    if (textBoxParams.input) {
      textBoxParams.input(args);
    }
  };

  return (
    <div
      className={clsx(
        'op-textbox-wrapper',
        isLabelInside && 'op-textbox-wrapper-label-inside',
        isDisabled && 'op-textbox-wrapper-disabled',
        isDisabled && isLabelInside &&
          'op-textbox-wrapper-disabled-label-inside',
      )}
    >
      {label && (
        <label
          className={clsx(
            'op-textbox-label',
            isLabelInside && 'op-textbox-label-inside',
          )}
        >
          {label}
          <span className="op-textbox-asterisk">{asterisk}</span>
        </label>
      )}

      <TextBoxComponent
        {...textBoxParams}
        className={clsx(
          'op-textbox',
          errorMessage && 'op-textbox-error-input',
          isLabelInside && 'op-textbox-label-inside-input',
        )}
        enabled={enabled}
        input={handleInput}
        value={internalValue} // Controlled strictly by local state
      />

      {errorMessage && (
        <span className="op-textbox-error-message">{errorMessage}</span>
      )}
    </div>
  );
}